export type MarketingQuizScoreKey = "selfService" | "guidedStart" | "opening" | "lowFit"

export type MarketingQuizOption = {
  id: string
  tags?: string[]
  score?: Partial<Record<MarketingQuizScoreKey, number>>
}

export type MarketingQuizQuestion = {
  id: string
  multiple?: boolean
  options: MarketingQuizOption[]
}

export type MarketingQuizOutcome = {
  id: string
  tags?: string[]
  withoutTags?: string[]
  minScore?: Partial<Record<MarketingQuizScoreKey, number>>
}

export type MarketingQuiz = {
  slug: string
  formNameKey: string
  trafficSourceKey: string
  questions: MarketingQuizQuestion[]
  outcomes: MarketingQuizOutcome[]
}

export type MarketingQuizAnswers = Record<string, string[]>

export type MarketingQuizResult = {
  outcomeId: string
  tags: string[]
  scores: Record<MarketingQuizScoreKey, number>
}

export const defaultMarketingQuizSlug = "start"

const emptyScores: Record<MarketingQuizScoreKey, number> = {
  selfService: 0,
  guidedStart: 0,
  opening: 0,
  lowFit: 0,
}

export const marketingQuizzes: Record<string, MarketingQuiz> = {
  [defaultMarketingQuizSlug]: {
    slug: defaultMarketingQuizSlug,
    formNameKey: "quizzes.start.form_name",
    trafficSourceKey: "quizzes.start.traffic_source",
    questions: [
      {
        id: "business_stage",
        options: [
          { id: "active_store", tags: ["active_store"], score: { selfService: 3 } },
          { id: "opening_now", tags: ["opening_now"], score: { opening: 3, selfService: 1 } },
          { id: "planning", tags: ["planning"], score: { opening: 1, lowFit: 2 } },
          { id: "curious", tags: ["curious"], score: { lowFit: 3 } },
        ],
      },
      {
        id: "business_type",
        options: [
          { id: "fashion", tags: ["retail_business"], score: { selfService: 2 } },
          { id: "beauty", tags: ["retail_business"], score: { selfService: 2 } },
          { id: "electronics", tags: ["retail_business"], score: { selfService: 2 } },
          { id: "home_decor", tags: ["retail_business"], score: { selfService: 2 } },
          { id: "virtual_store", tags: ["retail_business", "online_operation"], score: { selfService: 2 } },
          { id: "other_retail", tags: ["retail_business"], score: { selfService: 1 } },
        ],
      },
      {
        id: "catalog_assets",
        options: [
          { id: "organized_catalog", tags: ["has_catalog_assets"], score: { selfService: 3 } },
          { id: "pdf_or_images", tags: ["has_catalog_assets"], score: { selfService: 2, guidedStart: 1 } },
          { id: "other_system", tags: ["has_catalog_assets", "migration"], score: { selfService: 2, guidedStart: 1 } },
          { id: "not_ready", tags: ["catalog_not_ready"], score: { opening: 2, guidedStart: 2 } },
        ],
      },
      {
        id: "setup_preference",
        options: [
          { id: "do_it_myself", tags: ["self_service_intent"], score: { selfService: 3 } },
          { id: "follow_tutorial", tags: ["self_service_intent"], score: { selfService: 2 } },
          { id: "want_guidance", tags: ["guidance_intent"], score: { guidedStart: 3 } },
          { id: "team_setup", tags: ["expects_guidance"], score: { guidedStart: 4 } },
        ],
      },
      {
        id: "sales_channels",
        multiple: true,
        options: [
          { id: "physical_store", tags: ["physical_store"], score: { selfService: 1 } },
          { id: "whatsapp", tags: ["whatsapp"], score: { selfService: 1 } },
          { id: "instagram", tags: ["social_sales"], score: { selfService: 1 } },
          { id: "marketplace", tags: ["marketplace"], score: { selfService: 1 } },
          { id: "not_selling", tags: ["not_selling"], score: { opening: 2, lowFit: 1 } },
        ],
      },
      {
        id: "start_intent",
        options: [
          { id: "today", tags: ["start_now"], score: { selfService: 3 } },
          { id: "this_week", tags: ["start_soon"], score: { selfService: 2 } },
          { id: "after_catalog", tags: ["start_after_catalog"], score: { guidedStart: 2, opening: 1 } },
          { id: "researching", tags: ["researching"], score: { lowFit: 2 } },
        ],
      },
    ],
    outcomes: [
      {
        id: "qualified_self_service",
        minScore: { selfService: 10 },
        tags: ["active_store", "self_service_intent", "has_catalog_assets"],
      },
      {
        id: "guided_start",
        minScore: { guidedStart: 5 },
      },
      {
        id: "opening",
        minScore: { opening: 4 },
      },
      {
        id: "explore",
      },
    ],
  },
}

export const getMarketingQuiz = (slug?: string) =>
  marketingQuizzes[slug || defaultMarketingQuizSlug] ?? marketingQuizzes[defaultMarketingQuizSlug]

export const evaluateMarketingQuiz = (quiz: MarketingQuiz, answers: MarketingQuizAnswers): MarketingQuizResult => {
  const tags = new Set<string>()
  const scores = { ...emptyScores }

  quiz.questions.forEach((question) => {
    const selectedOptionIds = answers[question.id] ?? []

    question.options
      .filter((option) => selectedOptionIds.includes(option.id))
      .forEach((option) => {
        option.tags?.forEach((tag) => tags.add(tag))

        Object.entries(option.score ?? {}).forEach(([key, value]) => {
          scores[key as MarketingQuizScoreKey] += value ?? 0
        })
      })
  })

  const tagList = [...tags]
  const outcome = quiz.outcomes.find((candidate) => {
    const hasRequiredTags = (candidate.tags ?? []).every((tag) => tags.has(tag))
    const hasBlockedTags = (candidate.withoutTags ?? []).some((tag) => tags.has(tag))
    const hasRequiredScores = Object.entries(candidate.minScore ?? {}).every(
      ([key, value]) => scores[key as MarketingQuizScoreKey] >= (value ?? 0),
    )

    return hasRequiredTags && !hasBlockedTags && hasRequiredScores
  })

  return {
    outcomeId: outcome?.id ?? quiz.outcomes[quiz.outcomes.length - 1]?.id ?? "explore",
    tags: tagList,
    scores,
  }
}
